export type EmailFrequency = 'daily' | 'weekly' | 'monthly';

/** User-facing frequencies. (Pre-S5 had `hourly` as an admin-only escape hatch — dropped.) */
export type UserEmailFrequency = 'daily' | 'weekly' | 'monthly';

/** Supported languages for the email report */
export type EmailLanguage = 'fr' | 'en' | 'es' | 'de' | 'it' | 'pt';

export const EMAIL_LANGUAGES: { code: EmailLanguage; label: string }[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
];

export const DEFAULT_EMAIL_LANGUAGE: EmailLanguage = 'fr';

export interface EmailRecipient {
  email: string;
  language: EmailLanguage;
  /** Per-recipient cadence (S5). Each recipient picks their own rhythm. */
  frequency: EmailFrequency;
}

/** Normalize recipients stored in DDB — supports legacy `string[]` and new `EmailRecipient[]`. */
/**
 * Hard cap on email-digest recipients per brand, ALL tiers (not a commercial
 * lever — a deliverability/abuse guard). Enforced server-side in
 * `BrandsService.setEmailRecipients` and mirrored in the recipients dialog UI.
 */
export const MAX_EMAIL_RECIPIENTS = 10;

export function normalizeRecipients(raw: unknown): EmailRecipient[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => {
    if (typeof r === 'string') {
      return { email: r, language: DEFAULT_EMAIL_LANGUAGE, frequency: 'weekly' as EmailFrequency };
    }
    if (r && typeof r === 'object' && typeof (r as any).email === 'string') {
      const lang = (r as any).language;
      const validLang: EmailLanguage = ['fr', 'en', 'es', 'de', 'it', 'pt'].includes(lang) ? lang : DEFAULT_EMAIL_LANGUAGE;
      const freq = (r as any).frequency;
      const validFreq: EmailFrequency = ['daily', 'weekly', 'monthly'].includes(freq) ? freq : 'weekly';
      return { email: (r as any).email, language: validLang, frequency: validFreq };
    }
    return null;
  }).filter((r): r is EmailRecipient => r !== null);
}

/**
 * Brand-level email digest config (S5). Replaces the deleted EmailSchedule
 * table. Lives on the brand row alongside `auditConfig`.
 *
 * `scheduleArns` is the materialized state — one EventBridge Scheduler schedule
 * per recipient (keyed by lowercased email). Written by EmailScheduleSyncService
 * after every recipient mutation; read by tenant purge to clean up schedules
 * without a `ListSchedules` call.
 *
 * Design rationale: docs/scheduler-redesign-phases-s2-s6.md §0.2 D-10.
 */
export interface BrandEmailConfig {
  enabled: boolean;
  recipients: EmailRecipient[];
  scheduleArns?: Record<string, string>;
  /** Last-send timestamp per recipient (idempotency in worker, also useful for diagnostics). */
  lastSent?: Record<string, string>;
}

/**
 * EventBridge Scheduler payload — one message per recipient invocation.
 * The email-worker SQS queue receives these and renders + sends the digest.
 */
export interface EmailSummaryJob {
  tenantId: string;
  brandId: string;
  email: string;
  language: EmailLanguage;
  frequency: EmailFrequency;
  /** ISO timestamp of the schedule fire — used for idempotency in the worker. */
  triggeredFor: string;
}

/** Delta between two audit scores */
export interface ScoreDelta {
  label: string;
  previous: number | null;
  current: number;
  delta: number;
}

/** Data passed to the email template */
export interface EmailSummaryData {
  brandName: string;
  tenantName?: string;
  frequency: EmailFrequency;
  generatedAt: string;
  website?: {
    overallGeoScore: ScoreDelta;
    freshness: ScoreDelta;
    structuralSeo: ScoreDelta;
    contentQuality: ScoreDelta;
  };
  brandPerception?: {
    overallScore: ScoreDelta;
    narrativeScore: ScoreDelta;
    authorityScore: ScoreDelta;
    referralScore: ScoreDelta;
    sentimentBreakdown: Record<string, number>;
  };
  earnedMedia?: {
    overallScore: ScoreDelta;
    eeatScore: ScoreDelta;
    accuracyScore: ScoreDelta;
  };
  competitors?: Array<{
    name: string;
    score: number;
    delta: number;
  }>;
  completedTasks?: Array<{
    title: string;
    category: string;
    verificationStatus?: string;
  }>;
  newsIdeas?: Array<{
    title: string;
    topicTags: string[];
  }>;
  aiSummary: string;
}

// S1: scheduling utilities (computeTimeSlot, computeNextRun) deleted alongside
// the EmailSchedule type. Re-introduced in S5 if still needed for the new
// EventBridge Scheduler-based pipeline.
